/** ADM-02 File des inscriptions · ADM-03 Dossier d'inscription (décision validée : activation automatique si contrôles conformes). */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Choice, Pagination, SelectField, Table, useToast } from '@myron/design-system';
import { CheckCircle2, RefreshCw, XCircle } from 'lucide-react';
import type { Page, S } from '@myron/contracts';
import { get, post } from '../../../shared/api/client';
import { Confirmation, EntetePage, EtatRequete, ErreurFormulaire, MentionSimule, StatutBadge } from '../../../shared/ui/composants';
import { dateCourte, dateHeure, LIBELLE_MOYEN } from '../../../shared/ui/format';
import { useGroupes } from './commun';

type Entreprise = S<'EntrepriseAdmin'>;
type Dossier = { entreprise: Entreprise; demandeur?: S<'UtilisateurClient'>; verifications: { source: string; requeteLe: string; resultat: string; details: Record<string, unknown> }[] };

export function PageInscriptions() {
  const nav = useNavigate();
  const [page, setPage] = useState(1);
  const [statut, setStatut] = useState('A_VERIFIER,CONFORME');
  const q = useQuery({ queryKey: ['admin', 'inscriptions', statut, page], queryFn: () => get<Page<Entreprise>>('/admin/inscriptions', { statut, page, taille: 25 }) });
  return (
    <>
      <EntetePage titre="Inscriptions professionnelles" sousTitre="Les dossiers conformes sont activés automatiquement (décision validée). Cette file contient les dossiers à vérifier manuellement." />
      <div style={{ maxWidth: 320, marginBottom: 'var(--space-4)' }}>
        <SelectField label="Dossiers" value={statut} onChange={(e) => { setStatut(e.target.value); setPage(1); }} options={[
          { value: 'A_VERIFIER,CONFORME', label: 'À traiter' }, { value: 'A_VERIFIER', label: 'À vérifier' }, { value: 'CONFORME', label: 'Conformes en attente' },
          { value: 'REJETEE', label: 'Rejetés' }, { value: 'ACTIVE', label: 'Activés' }, { value: 'EMAIL_A_VERIFIER', label: 'E-mail non vérifié' },
        ]} />
      </div>
      <EtatRequete requete={q} vide={(d) => d.total === 0} titreVide="Aucun dossier" texteVide="Aucune inscription ne correspond à ce filtre." enfants={(d) => (
        <>
          <Table<Entreprise>
            legende="Inscriptions"
            lignes={d.donnees}
            cle={(e) => e.id!}
            onLigne={(e) => nav(`/admin/inscriptions/${e.id}`)}
            colonnes={[
              { cle: 'ref', titre: 'Référence', rendu: (e) => e.reference },
              { cle: 'rs', titre: 'Raison sociale', rendu: (e) => e.raisonSociale },
              { cle: 'siret', titre: 'SIRET', rendu: (e) => <span className="my-num">{e.siret}</span> },
              { cle: 'naf', titre: 'NAF', rendu: (e) => e.codeNaf },
              { cle: 'statut', titre: 'Statut', rendu: (e) => <StatutBadge statut={e.statut!} /> },
              { cle: 'date', titre: 'Déposé le', rendu: (e) => dateCourte(e.creeLe) },
              { cle: 'a', titre: <span className="my-sr">Actions</span>, actions: true, rendu: (e) => <Button compact variante="secondaire" onClick={(ev) => { ev.stopPropagation(); nav(`/admin/inscriptions/${e.id}`); }} aria-label={`Ouvrir le dossier ${e.raisonSociale}`}>Ouvrir</Button> },
            ]}
          />
          <Pagination page={d.page} total={d.total} taille={d.taille} onPage={setPage} />
        </>
      )} />
    </>
  );
}

export function PageDossierInscription() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['admin', 'inscription', id], queryFn: () => get<Dossier>(`/admin/inscriptions/${id}`) });
  const groupes = useGroupes();
  const [groupe, setGroupe] = useState('');
  const [moyens, setMoyens] = useState<string[]>(['CB_EN_LIGNE', 'CB_RETRAIT']);
  const [rejet, setRejet] = useState(false);
  const rafraichir = (d: unknown) => { void qc.invalidateQueries({ queryKey: ['admin'] }); return d; };

  const relancer = useMutation({ mutationFn: () => post<Dossier>(`/admin/inscriptions/${id}/controles`), onSuccess: (d) => { qc.setQueryData(['admin', 'inscription', id], d); rafraichir(d); toast({ ton: 'info', titre: 'Contrôles relancés', texte: `Nouveau statut : ${d.entreprise.statut}` }); } });
  const decider = useMutation({
    mutationFn: (v: { decision: 'VALIDER' | 'REJETER'; motif?: string }) => post<Entreprise>(`/admin/inscriptions/${id}/decision`, { ...v, ...(v.decision === 'VALIDER' ? { groupeTarifaireId: groupe || undefined, moyensPaiementAutorises: moyens } : {}) }, { version: q.data!.entreprise.version }),
    onSuccess: (e) => { setRejet(false); void q.refetch(); rafraichir(e); toast({ ton: e.statut === 'ACTIVE' ? 'succes' : 'info', titre: e.statut === 'ACTIVE' ? 'Compte activé' : 'Dossier rejeté' }); },
  });

  return (
    <EtatRequete requete={q} enfants={(d) => {
      const e = d.entreprise;
      const decidable = e.statut === 'A_VERIFIER' || e.statut === 'CONFORME';
      return (
        <>
          <EntetePage titre={e.raisonSociale ?? ""} fil={[{ libelle: 'Inscriptions', vers: '/admin/inscriptions' }, { libelle: e.reference ?? '' }]} sousTitre={<StatutBadge statut={e.statut!} />} />
          <div className="my-grille-2">
            <div className="my-pile">
              <Card as="section" aria-labelledby="t-ent">
                <h2 id="t-ent" className="my-titre-4">Entreprise</h2>
                <dl className="my-dl" style={{ marginTop: 12 }}>
                  <dt>SIRET</dt><dd className="my-num">{e.siret}</dd>
                  <dt>Code NAF</dt><dd>{e.codeNaf ?? '—'}</dd>
                  <dt>Forme juridique</dt><dd>{e.formeJuridique ?? '—'}</dd>
                  <dt>Demandeur</dt><dd>{d.demandeur ? `${d.demandeur.prenom} ${d.demandeur.nom} — ${d.demandeur.email}` : '—'}</dd>
                  <dt>Déposé le</dt><dd>{dateHeure(e.creeLe)}</dd>
                  {e.motifRejet && <><dt>Motif de rejet</dt><dd>{e.motifRejet}</dd></>}
                </dl>
              </Card>
              <Card as="section" aria-labelledby="t-ctl">
                <div className="my-ligne my-ligne--espace">
                  <h2 id="t-ctl" className="my-titre-4">Contrôles automatiques</h2>
                  <MentionSimule>Sirene SIMULÉE</MentionSimule>
                </div>
                {d.verifications.length === 0 ? <p className="my-secondaire">Aucun contrôle effectué (e-mail non vérifié).</p> : (
                  <ul className="my-pile my-pile--serre" style={{ listStyle: 'none', padding: 0, marginTop: 12 }}>
                    {d.verifications.map((v, i) => (
                      <li key={i} className="my-ligne">
                        <StatutBadge statut={v.resultat === 'REJETE' ? 'REJETEE' : v.resultat} />
                        <span className="my-petit">{dateHeure(v.requeteLe)} — {((v.details.motifs as string[] | undefined) ?? []).join(' ; ') || 'Aucune anomalie'}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {decidable && <div style={{ marginTop: 12 }}><Button variante="secondaire" icone={RefreshCw} chargement={relancer.isPending} onClick={() => relancer.mutate()}>Relancer les contrôles</Button></div>}
                <ErreurFormulaire erreur={relancer.error} />
              </Card>
            </div>
            <Card as="section" aria-labelledby="t-dec">
              <h2 id="t-dec" className="my-titre-4">Décision</h2>
              {!decidable ? <Alert ton="info">Ce dossier n’est pas en attente de décision.</Alert> : (
                <div className="my-pile" style={{ marginTop: 12 }}>
                  {e.statut === 'A_VERIFIER' && <Alert ton="alerte" titre="Vérification manuelle requise">Les contrôles automatiques n’ont pas pu conclure (base Sirene indisponible ou données à confirmer).</Alert>}
                  <SelectField label="Groupe tarifaire" value={groupe} onChange={(ev) => setGroupe(ev.target.value)} options={[{ value: '', label: 'Prix de base (aucun groupe)' }, ...(groupes.data ?? []).map((g) => ({ value: g.id, label: `${g.code} — ${g.libelle}` }))]} />
                  <fieldset className="my-pile my-pile--serre" style={{ border: 0, padding: 0, margin: 0 }}>
                    <legend className="my-field__label">Moyens de paiement autorisés</legend>
                    {['VIREMENT', 'CB_EN_LIGNE', 'CB_RETRAIT'].map((m) => (
                      <Choice key={m} label={LIBELLE_MOYEN[m]} checked={moyens.includes(m)} onChange={(ev) => setMoyens((l) => (ev.target.checked ? [...l, m] : l.filter((x) => x !== m)))} />
                    ))}
                  </fieldset>
                  <ErreurFormulaire erreur={!rejet ? decider.error : null} />
                  <div className="my-ligne">
                    <Button icone={CheckCircle2} chargement={decider.isPending && decider.variables?.decision === 'VALIDER'} disabled={!moyens.length} onClick={() => decider.mutate({ decision: 'VALIDER' })}>Valider et activer</Button>
                    <Button variante="danger" icone={XCircle} onClick={() => setRejet(true)}>Rejeter</Button>
                  </div>
                </div>
              )}
            </Card>
          </div>
          <Confirmation ouverte={rejet} titre="Rejeter la demande" texte="Le demandeur sera informé du motif par e-mail." libelleAction="Rejeter le dossier" danger motif="obligatoire" chargement={decider.isPending} erreur={rejet ? decider.error : null} onFermer={() => setRejet(false)} onConfirmer={(motif) => decider.mutate({ decision: 'REJETER', motif })} />
        </>
      );
    }} />
  );
}
